/* eslint-disable */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useBrandStore } from "@/stores/brand.js";
import { useRewardSetupStore } from "@/stores/rewardSetup.js";
import {
  formatFEtoBESms,
  formatFEToDisplay,
  validCountrySmsRestriction,
} from "@/constants/smsUtils.js";

export const useSmsScreenStore = defineStore("smsScreen", () => {
  const refetch = ref(false);
  const query = ref(null);
  const step = ref(0);
  const campaignType = ref(null);
  const filteringOption = ref([]);
  const selectedUsers = ref(0);
  const brandStore = useBrandStore();
  const rewardStore = useRewardSetupStore();
  const overallUsers = ref(0);
  const brandId = ref(null);
  const brandName = ref(null);
  const sbsc = ref(false);
  const countryCode = ref("MY");
  const foreignEnabled = ref(false);
  const filterArgs = ref({
    spec: null,
    opt: null,
  });
  const importFilterArgs = ref({
    spec: "",
    target: "",
    value: "",
    validData: [],
  });
  const productInclusive = ref(true);
  const msgArgs = ref({
    title: null,
    expiryDate: null,
    smsContentInput: "",
    namePlaceholder: "",
    emailDesign: null,
    emailHtml: "",
    emailSubject: "",
    emailPreheader: "",
    emailTemplateId: null,
    customFont: false,
    customFontFamily: null,
  });
  const promotionArgs = ref({});
  const announcementArgs = ref({});
  const scheduleArgs = ref({});
  const storeList = ref([]);
  const membershipList = ref([]);
  const modal = ref(null);
  const rechargeWarningModal = ref(null);
  const smsTest = ref(false);
  const emailTest = ref(false);
  const blacklistedUsers = ref([]);
  const blacklistedNumbers = ref([]);
  const testNumber = ref("");
  const brandEmail = ref(null);
  const broadcastSent = ref(false);
  const maxValidSms = ref(0);
  const maxValidWs = ref(0);
  const maxValidEmail = ref(0);
  const messageCountLoading = ref(true);
  const lastVisitDates = ref([]);
  const channelType = ref("sms");
  const wabaVerified = ref(false);
  const wsOverviewData = ref({});
  const selectedTemplate = ref(null);
  const selectedCategory = ref("");
  const selectedProducts = ref([]);
  const countLoading = ref(false);
  const importLoading = ref(false);
  const emailCtaCount = ref(0);

  const remainingMessage = computed(() => {
    const sent = wsOverviewData.value?.dailySent ?? 0;
    const quota = wsOverviewData.value?.dailyQuota ?? 0;
    if (!quota) return 0;
    return Number(quota - sent);
  });

  const clearFile = ref(() => {}); // use to clear uploaded file to reset file upload

  function startOver() {
    refetch.value = true;
    step.value = 0;
    campaignType.value = null;
    filteringOption.value = [];
    selectedUsers.value = 0;
    filterArgs.value = {
      spec: null,
      opt: null,
    };
    importFilterArgs.value = {
      spec: "",
      target: "",
      value: "",
      validData: [],
    };
    msgArgs.value = {
      title: null,
      expiryDate: null,
      smsContentInput: "",
      namePlaceholder: "",
      emailDesign: null,
      emailHtml: "",
      emailSubject: "",
      emailPreheader: "",
      emailTemplateId: null,
      customFont: false,
      customFontFamily: null,
    };
    promotionArgs.value = {};
    announcementArgs.value = {};
    scheduleArgs.value = {};
    smsTest.value = false;
    emailTest.value = false;
    broadcastSent.value = false;
    emailCtaCount.value = 0;
    sbsc.value = false;
    countLoading.value = false;
    importLoading.value = false;
    //BOOTSTRAP hide does not get rid of modal backdrop by default
    if (modal.value) {
      modal.value._element.addEventListener("shown.bs.hidden", (event) => {
        modal.value.dispose();
      });
      modal.value.hide();
      modal.value = null;
    }
  }

  const navSteps = computed(() => [
    {
      id: 1,
      name: "Choose Audience",
    },
    {
      id: 2,
      name: "Campaign Details",
    },
    {
      id: 3,
      name: channelType.value === "email" ? "Email Content" : "Message Content",
    },
    {
      id: 4,
      name: "Schedule Broadcast",
    },
    {
      id: 5,
      name: "Confirmation",
    },
  ]);

  const validMsgArgs = computed(() => {
    const { title, expiryDate } = msgArgs.value;

    const hasTitle = !!title && title.trim().length > 0;
    const hasDate = !!expiryDate && expiryDate.trim().length > 0;

    if (campaignType.value === "manual") {
      return hasTitle && hasDate;
    }

    return hasDate;
  });

  const validPromotionArgs = computed(() => {
    const validName = promotionArgs.value.name !== "";
    const validCost = promotionArgs.value.costStructureType !== "";
    const validValue =
      promotionArgs.value.costStructureValue !== "" &&
      !isNaN(promotionArgs.value.costStructureValue) &&
      promotionArgs.value.costStructureValue !== 0 &&
      !(
        promotionArgs.value.costStructureType === "percentage_discount" &&
        promotionArgs.value.costStructureValue === 100
      );

    if (Object.keys(promotionArgs.value).length === 0) {
      return false;
    } else {
      return validName && validCost && validValue;
    }
  });

  const validAnnouncementArgs = computed(() => {
    if (Object.keys(announcementArgs.value).length === 0) {
      return false;
    } else {
      return (
        announcementArgs.value.trackingExpiry !== "" &&
        announcementArgs.value.trackingExpiry !== null
      );
    }
  });

  const validSchedule = computed(() => {
    const validType = scheduleArgs.value.scheduleType != "";
    const validTiming =
      scheduleArgs.value.scheduleType == "now"
        ? true
        : scheduleArgs.value.sendDate !== "" &&
          scheduleArgs.value.sendTime !== "";

    if (Object.keys(scheduleArgs.value).length === 0) {
      return false;
    } else {
      return validType && validTiming;
    }
  });

  const withinLimit = computed(() => {
    const isWhatsApp = channelType.value === "whatsapp";
    const isEmail = channelType.value === "email";
    const userCount = selectedUsers.value;

    if (isEmail) return maxValidEmail.value >= userCount;
    if (isWhatsApp) return maxValidWs.value >= userCount;

    return maxValidSms.value >= userCount;
  });

  const canSendBroadcast = computed(() => {
    const isWhatsApp = channelType.value === "whatsapp";
    const isEmail = channelType.value === "email";

    if (broadcastSent.value) return false;

    if (isEmail) {
      return emailTest.value;
    }

    if (isWhatsApp) {
      const testedRecently = selectedTemplate.value?.testedRecently;
      return testedRecently || smsTest.value;
    } else {
      return smsTest.value;
    }
  });

  const isEmailValid = computed(() => {
    const {
      validEmail,
      emailSubject,
      emailPreheader,
      emailHtml,
      namePlaceholder,
      customFont,
      customFontFamily,
    } = msgArgs.value;

    const hasContent =
      emailSubject.trim() !== "" &&
      emailPreheader.trim() !== "" &&
      emailHtml.trim() !== "";
    const hasRequiredPlaceholder = emailHtml.includes("%recipient.name%")
      ? namePlaceholder
      : true;
    const hasRequiredFont = customFont ? !!customFontFamily : true;

    return (
      validEmail === true &&
      hasContent &&
      !!hasRequiredPlaceholder &&
      hasRequiredFont
    );
  });

  const canForward = computed(() => {
    if (step.value === 0) {
      return campaignType.value !== null && filteringOption.value.length > 0;
    } else if (step.value === 1) {
      return (
        filterArgs.value.spec !== null &&
        filterArgs.value.opt !== null &&
        selectedUsers.value > 0 &&
        ((filterArgs.value.spec === "importing" && !importLoading.value) ||
          (filterArgs.value.spec !== "importing" && !countLoading.value)) &&
        !(
          channelType.value === "whatsapp" &&
          selectedUsers.value > remainingMessage.value
        )
      );
    } else if (step.value === 2) {
      if (campaignType.value === "announcement") {
        return validAnnouncementArgs.value && msgArgs.value.title;
      } else {
        return (
          validPromotionArgs.value &&
          validMsgArgs.value &&
          rewardStore.canContinue
        );
      }
    } else if (step.value === 3) {
      if (channelType.value === "whatsapp") {
        return selectedTemplate.value !== null;
      } else if (channelType.value === "email") {
        // Email broadcasts need subject + valid content
        return isEmailValid.value;
      } else {
        return (
          validCountrySmsRestriction(
            brandStore.brand.countryCode,
            dbMessage.value
          ) && msgArgs.value?.validSms
        );
      }
    } else if (step.value === 4) {
      return validSchedule.value;
    } else if (step.value === 5) {
      return canSendBroadcast.value;
    } else {
      return false;
    }
    lastVisitDates.value = [];
  });

  function moveForward() {
    step.value = step.value + 1;
  }

  function moveBackward() {
    step.value = step.value - 1;
  }

  const inputMessage = computed(() => {
    return msgArgs.value.smsContentInput;
  });

  const fullMessage = computed(() => {
    return formatFEToDisplay(
      msgArgs.value.smsContentInput,
      promotionArgs.value.name,
      msgArgs.value.namePlaceholder
    );
  });

  const dbMessage = computed(() => {
    return formatFEtoBESms(msgArgs.value.smsContentInput);
  });

  function resetOnCampaignChg() {
    announcementArgs.value = {};
    promotionArgs.value = {};
    msgArgs.value = {
      title: null,
      expiryDate: null,
      smsContentInput: "",
      namePlaceholder: "",
      emailDesign: null,
      emailHtml: "",
      emailSubject: "",
      emailPreheader: "",
      emailTemplateId: null,
      customFont: false,
      customFontFamily: null,
    };
  }

  function formattedFilterArgs(args) {
    let formattedFilter;

    if (args.spec == "filtering") {
      formattedFilter = args.opt.map((item) => {
        return {
          action: item.action,
          input: item.filter.type + ";" + item.filter.input,
        };
      });
    } else if (args.spec == "frequency") {
      formattedFilter = args.opt.map((item) => {
        let arg;
        switch (item) {
          case "firstVisits":
            arg = "frequency;equal;1";
            break;
          case "secondVisits":
            arg = "frequency;equal;2";
            break;
          case "thirdVisits":
            arg = "frequency;equal;3";
            break;
          case "multiVisits":
            arg = "frequency;more;3";
            break;
        }
        return {
          action: "include",
          input: arg,
        };
      });
    } else if (args.spec == "inactive") {
      formattedFilter = args.opt.map((item) => {
        const input = item.split(";")[1].split("_").join(";");
        let arg = "inactive;";

        if (item.split(";")[1].split("_").length > 1) {
          arg = arg + "between;" + input;
        } else {
          arg = arg + "more;" + `${parseInt(input) - 1}`;
        }

        return {
          action: "include",
          input: arg,
        };
      });
    } else if (args.spec == "total_spending") {
      formattedFilter = args.opt.map((item) => {
        return {
          action: "include",
          input: "spending;" + item,
        };
      });
    } else if (
      args.spec == "available_points" ||
      args.spec == "collected_points" ||
      args.spec == "collected_stamps" ||
      args.spec == "stores" ||
      args.spec == "membership"
    ) {
      formattedFilter = args.opt.map((item) => {
        return {
          action: "include",
          input: `${args.spec};` + item,
        };
      });
    } else if (args.spec == "age_group") {
      formattedFilter = args.opt.map((item) => {
        let arg;
        switch (item) {
          case "lessThan18":
            arg = "age_group;between;0;18";
            break;
          case "18to24":
            arg = "age_group;between;18;24";
            break;
          case "25to34":
            arg = "age_group;between;25;34";
            break;
          case "35to55":
            arg = "age_group;between;35;55";
            break;
          case "55above":
            arg = "age_group;more;55";
            break;
        }
        return {
          action: "include",
          input: arg,
        };
      });
    } else if (args.spec == "last_visit") {
      formattedFilter = [
        {
          action: "include",
          input: "last_visit;" + args.opt,
        },
      ];
    } else if (args.spec == "website_visit") {
      formattedFilter = args.opt.map((token) => {
        return {
          action: "include",
          input: `website_visit;${token}`,
        };
      });
    } else if (args.spec == "referral_campaign") {
      formattedFilter = {
        action: "include",
        input: `referral_campaign;${args.opt.join(";")}`,
      };
    } else if (args.spec == "review_campaign") {
      formattedFilter = {
        action: "include",
        input: `review_campaign;${args.opt.join(";")}`,
      };
    } else if (args.spec == "opus") {
      formattedFilter = {
        action: "opus",
        input: JSON.stringify(args.opt),
      };
    } else if (args.spec == "gender_group") {
      formattedFilter = args.opt.map((item) => {
        return {
          action: "include",
          input: `gender_group;${item}`,
        };
      });
    } else if (args.spec == "ethnicity_group") {
      formattedFilter = args.opt.map((item) => {
        return {
          action: "include",
          input: `ethnicity_group;${item}`,
        };
      });
    } else {
      formattedFilter = null;
    }

    return formattedFilter;
  }

  return {
    brandStore,
    overallUsers,
    brandId,
    brandName,
    countryCode,
    step,
    navSteps,
    campaignType,
    filteringOption,
    filterArgs,
    importFilterArgs,
    productInclusive,
    canForward,
    moveForward,
    moveBackward,
    msgArgs,
    promotionArgs,
    scheduleArgs,
    selectedUsers,
    storeList,
    inputMessage,
    fullMessage,
    modal,
    rechargeWarningModal,
    dbMessage,
    announcementArgs,
    smsTest,
    broadcastSent,
    sbsc,
    emailTest,
    resetOnCampaignChg,
    startOver,
    blacklistedUsers,
    blacklistedNumbers,
    testNumber,
    brandEmail,
    refetch,
    query,
    clearFile,
    membershipList,
    maxValidSms,
    maxValidWs,
    maxValidEmail,
    messageCountLoading,
    withinLimit,
    lastVisitDates,
    formattedFilterArgs,
    foreignEnabled,
    channelType,
    wsOverviewData,
    remainingMessage,
    wabaVerified,
    selectedTemplate,
    selectedCategory,
    selectedProducts,
    countLoading,
    importLoading,
    emailCtaCount,
  };
});
